/**
 * Logo — the Ballast Payroll brand lockup (mark + wordmark).
 *
 * Uses the navy mark (/ballast-mark.svg) on light backgrounds and the white mark
 * (/ballast-mark-white.svg) on dark ones. `stacked` centers the mark above the
 * wordmark for auth/marketing hero use; the default is a horizontal lockup for
 * nav headers. The <img> is decorative (alt="") since the wordmark text is present.
 *
 * @module components/Logo
 */

import type { ReactElement } from 'react';

interface LogoProps {
  /** Mark height. `xs` is the compact app-bar size; `md`+ suit marketing/heroes. */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Stack the mark above the wordmark (centered). */
  stacked?: boolean;
  /** Use the white mark + white text for dark backgrounds. */
  white?: boolean;
}

const MARK_H: Record<NonNullable<LogoProps['size']>, string> = {
  xs: 'h-7',
  sm: 'h-9',
  md: 'h-11',
  lg: 'h-20',
};

/** Wordmark text size paired with each mark size. */
const TEXT_SIZE: Record<NonNullable<LogoProps['size']>, string> = {
  xs: 'text-lg',
  sm: 'text-2xl',
  md: 'text-[1.7rem]',
  lg: 'text-4xl',
};

export function Logo({ size = 'md', stacked = false, white = false }: LogoProps): ReactElement {
  const markSrc = white ? '/ballast-mark-white.svg' : '/ballast-mark.svg';
  const textColor = white ? 'text-white' : 'text-brand-800';

  if (stacked) {
    return (
      <div className="flex flex-col items-center">
        <img src={markSrc} alt="" className={`${MARK_H[size]} w-auto mb-4`} />
        <span className={`${TEXT_SIZE[size]} font-extrabold tracking-tight whitespace-nowrap ${textColor}`}>
          Ballast Payroll
        </span>
      </div>
    );
  }

  return (
    <span className={`inline-flex items-center ${size === 'xs' ? 'gap-2' : 'gap-2.5'}`}>
      <img src={markSrc} alt="" className={`${MARK_H[size]} w-auto`} />
      <span className={`${TEXT_SIZE[size]} font-extrabold tracking-tight whitespace-nowrap ${textColor}`}>
        Ballast Payroll
      </span>
    </span>
  );
}
